import { Link, useNavigate } from 'react-router-dom';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Plane, Clock, Shield, CreditCard, Users, MapPin, CheckCircle2, ArrowRight } from 'lucide-react';

const AIRPORT_HERO = "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920";

const AirportTransfersPage = () => {
  const navigate = useNavigate();

  const handleBookClick = (e) => {
    e.preventDefault();
    navigate('/');
    setTimeout(() => {
      const bookingSection = document.getElementById('booking');
      if (bookingSection) {
        bookingSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }, 100);
  };

  const features = [
    {
      icon: <Clock className="w-8 h-8" />,
      title: "Flight Tracking",
      description: "We monitor your flight in real-time. If your flight is early or delayed, we adjust pickup accordingly – no extra charges."
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Meet & Greet",
      description: "For arrivals, your driver can meet you inside the terminal with a name board, helping with luggage and guiding you to the vehicle."
    },
    {
      icon: <CreditCard className="w-8 h-8" />,
      title: "Fixed Pricing",
      description: "Know exactly what you'll pay before you travel. Our airport transfer rates are fixed with no hidden surprises or surge pricing."
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "All Group Sizes",
      description: "Whether you're traveling solo or with a group of 11, we have sedans, SUVs, and maxi taxis ready for airport runs."
    }
  ];

  const airports = [
    {
      name: "Melbourne Airport (Tullamarine)",
      code: "MEL",
      distance: "~25km from CBD",
      description: "Victoria's main domestic and international airport. We provide 24/7 transfers to and from all terminals."
    },
    {
      name: "Avalon Airport",
      code: "AVV",
      distance: "~55km from CBD",
      description: "Located between Melbourne and Geelong, perfect for travelers in the western suburbs or heading to the Great Ocean Road."
    }
  ];

  return (
    <div data-testid="airport-page">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="pt-28 pb-20 relative"
        style={{
          backgroundImage: `linear-gradient(to bottom, rgba(26, 26, 26, 0.85), rgba(26, 26, 26, 0.7)), url(${AIRPORT_HERO})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="max-w-3xl text-white">
            <div className="flex items-center gap-2 mb-4">
              <Plane className="w-5 h-5 text-primary" />
              <span className="text-xs font-bold uppercase tracking-widest text-primary">Airport Transfers</span>
            </div>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight leading-tight">
              Reliable Airport <span className="text-primary">Transfers</span> Across Melbourne
            </h1>
            <p className="mt-6 text-gray-300 text-lg leading-relaxed">
              Start or end your journey stress-free with our professional airport transfer service. 
              Flight tracked, fixed prices, and available around the clock – we ensure you never miss a flight 
              or wait unnecessarily after landing.
            </p>
            <div className="flex flex-wrap gap-4 mt-8">
              <button 
                onClick={handleBookClick}
                className="bg-primary hover:bg-primary-hover text-white px-8 py-3 rounded-lg font-semibold transition-colors duration-200 inline-flex items-center gap-2"
              >
                Book Airport Transfer
                <ArrowRight className="w-4 h-4" />
              </button>
              <a 
                href="tel:1300096685"
                className="bg-white/10 hover:bg-white/20 text-white px-8 py-3 rounded-lg font-semibold transition-colors duration-200 border border-white/20"
              >
                Call 1300 096 685
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <span className="text-xs font-bold uppercase tracking-widest text-primary">Why Choose Us</span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-secondary mt-2">
              Airport Transfer Excellence
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="p-6 rounded-xl border border-gray-100 bg-white hover:border-primary hover:shadow-lg transition-all duration-300 group"
              >
                <div className="w-14 h-14 rounded-lg bg-orange-50 text-primary flex items-center justify-center mb-4 group-hover:bg-primary group-hover:text-white transition-colors duration-300">
                  {feature.icon}
                </div>
                <h3 className="font-heading text-xl font-bold text-secondary mb-2">{feature.title}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Airports We Service */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <span className="text-xs font-bold uppercase tracking-widest text-primary">Airports</span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-secondary mt-2">
              We Service All Melbourne Airports
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {airports.map((airport, index) => (
              <div key={index} className="bg-white p-8 rounded-xl shadow-sm">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-16 h-16 rounded-xl bg-secondary text-white flex items-center justify-center">
                    <span className="font-heading text-xl font-bold">{airport.code}</span>
                  </div>
                  <div>
                    <h3 className="font-heading text-xl font-bold text-secondary">{airport.name}</h3>
                    <p className="text-sm text-muted-foreground flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      {airport.distance}
                    </p>
                  </div>
                </div>
                <p className="text-muted-foreground leading-relaxed">{airport.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <span className="text-xs font-bold uppercase tracking-widest text-primary">Simple Process</span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-secondary mt-2">
              How Airport Transfers Work
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                step: "1",
                title: "Book Your Transfer",
                description: "Book online or call us with your flight details, pickup address, and passenger numbers. We'll confirm your booking instantly."
              },
              {
                step: "2",
                title: "We Track Your Flight",
                description: "On the day of travel, we monitor your flight status. For arrivals, your driver will be waiting even if your flight is delayed."
              },
              {
                step: "3",
                title: "Enjoy Your Ride",
                description: "Your professional driver will transport you safely to your destination. Sit back, relax, and leave the traffic to us."
              }
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 rounded-full bg-primary text-white flex items-center justify-center mx-auto mb-4 font-heading text-2xl font-bold">
                  {item.step}
                </div>
                <h3 className="font-heading text-xl font-bold text-secondary mb-2">{item.title}</h3>
                <p className="text-muted-foreground">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tips Section */}
      <section className="py-16 bg-secondary text-white">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <h2 className="font-heading text-3xl font-bold mb-8 text-center">
            Airport Transfer Tips
          </h2>
          <div className="space-y-4">
            {[
              "Book at least 24 hours in advance for guaranteed availability, especially during peak travel seasons.",
              "Provide your flight number when booking – we'll track your flight and adjust pickup time if needed.",
              "For international arrivals, allow 45-60 minutes after landing for customs and baggage collection.",
              "Let us know if you have oversized luggage, golf clubs, or special requirements when booking.",
              "Our drivers can meet you inside the terminal with a name board – just request meet & greet service."
            ].map((tip, index) => (
              <div key={index} className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                <p className="text-gray-300">{tip}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-primary">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-black mb-4">
            Ready to Book Your Airport Transfer?
          </h2>
          <p className="text-black/80 mb-8">
            Don't leave your airport transport to chance. Book with 13cabsbook for reliable, professional service.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={handleBookClick}
              className="bg-secondary hover:bg-secondary/90 text-white px-8 py-3 rounded-lg font-semibold transition-colors duration-200"
            >
              Book Online Now
            </button>
            <a 
              href="tel:1300096685"
              className="bg-white hover:bg-gray-100 text-secondary px-8 py-3 rounded-lg font-semibold transition-colors duration-200"
            >
              Call 1300 096 685
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AirportTransfersPage;
